import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import { getAnalytics } from '@/utils/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { CheckCircle2, Clock, AlertCircle, Package, FileImage, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { fadeUpAt } from '@/lib/motion';

// Brand palette derived from CSS vars
const CHART_COLORS = [
  'hsl(220, 60%, 18%)',     // navy
  'hsl(38, 48%, 50%)',       // gold
  'hsl(358, 55%, 42%)',     // burgundy
  'hsl(152, 22%, 38%)',     // sage
  'hsl(218, 35%, 28%)',     // navy-soft
  'hsl(38, 30%, 88%)',       // champagne
];

// Status specific colors for consistent charting and cards
const STATUS_COLORS_MAP = {
  'Pre-CAD': 'hsl(218, 15%, 55%)',          // Slate
  'In Progress': 'hsl(200, 70%, 45%)',      // Blue
  'CAD Completed': 'hsl(220, 60%, 25%)',    // Navy-soft
  'Accepted': 'hsl(142, 45%, 40%)',         // Sage / Green
  'Rejected': 'hsl(358, 65%, 45%)',         // Burgundy / Red
  'Revision Requested': 'hsl(38, 75%, 50%)', // Gold / Amber
  'On Hold': 'hsl(28, 70%, 45%)',           // Orange / Brown
};

const StatCard = ({ title, value, icon, accent = 'navy', delay = 0 }) => (
  <motion.div {...fadeUpAt(delay)}
    className="group relative bg-card border border-hairline rounded-lg p-6 transition-colors duration-base ease-out-expo hover:border-gold/60">
    <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-gold/0 via-gold/40 to-gold/0 opacity-0 group-hover:opacity-100 transition-opacity duration-base ease-out-expo" />
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <p className="eyebrow text-muted-foreground">{title}</p>
        <p className="font-display text-[36px] leading-none tracking-editorial text-navy-deep mt-3 tnum">{value}</p>
      </div>
      <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center border ${
        accent === 'gold' ? 'border-gold/40 bg-gold/10 text-gold-deep' :
        accent === 'burgundy' ? 'border-burgundy/30 bg-burgundy/8 text-burgundy' :
        accent === 'sage' ? 'border-sage/30 bg-sage/8 text-sage' :
        accent === 'blue' ? 'border-blue-400/40 bg-blue-500/10 text-blue-600' :
        'border-navy/30 bg-navy/5 text-navy'
      }`}>
        {icon}
      </div>
    </div>
  </motion.div>
);

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await getAnalytics();
      setAnalytics(response.data);
    } catch (error) {
      if (error.response?.status !== 401) toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <CollapsibleLayout>
        <div className="flex items-center justify-center h-96 bg-ivory">
          <div className="flex flex-col items-center gap-4">
            <div className="ks-loader" />
            <div className="eyebrow text-muted-foreground">LOADING</div>
          </div>
        </div>
      </CollapsibleLayout>
    );
  }

  const pieData = analytics ? [
    { name: 'Pre-CAD', value: analytics.pre_cad_tasks || 0 },
    { name: 'In Progress', value: analytics.in_progress_tasks || 0 },
    { name: 'CAD Completed', value: analytics.cad_completed_tasks || 0 },
    { name: 'Accepted', value: analytics.accepted_tasks || 0 },
    { name: 'Rejected', value: analytics.rejected_tasks || 0 },
    { name: 'Revision Requested', value: analytics.revision_tasks || 0 },
    { name: 'On Hold', value: analytics.on_hold_tasks || 0 },
  ].filter(d => d.value > 0) : [];

  return (
    <CollapsibleLayout>
      <div className="h-screen bg-ivory overflow-auto">
        <div className="max-w-7xl mx-auto p-6 space-y-6">
          {/* Header */}
          <motion.div {...fadeUpAt(0)} className="mb-2">
            <div className="eyebrow text-gold-deep">ANALYTICS</div>
            <h1 className="font-display text-[44px] leading-[1.02] tracking-editorial text-navy-deep mt-2">
              Studio <span className="font-display-italic text-gold-deep">Overview</span>
            </h1>
            <div className="h-px w-16 bg-gold mt-4 motion-rule" />
            <p className="text-[14px] text-muted-foreground mt-4 max-w-lg leading-relaxed">
              Performance, workload, and throughput across the product development pipeline.
            </p>
          </motion.div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard
              title="Total Tasks"
              value={analytics?.total_tasks || 0}
              icon={<Package size={18} />}
              accent="navy"
              delay={1}
            />
            <StatCard
              title="In Progress"
              value={analytics?.in_progress_tasks || 0}
              icon={<Clock size={18} />}
              accent="blue"
              delay={2}
            />
            <StatCard
              title="CAD Completed"
              value={analytics?.cad_completed_tasks || 0}
              icon={<FileImage size={18} />}
              accent="navy"
              delay={3}
            />
            <StatCard
              title="Accepted"
              value={analytics?.accepted_tasks || 0}
              icon={<CheckCircle2 size={18} />}
              accent="sage"
              delay={4}
            />
            <StatCard
              title="Rejected"
              value={analytics?.rejected_tasks || 0}
              icon={<XCircle size={18} />}
              accent="burgundy"
              delay={5}
            />
            <StatCard
              title="Revision Requested"
              value={analytics?.revision_tasks || 0}
              icon={<AlertCircle size={18} />}
              accent="gold"
              delay={6}
            />
          </div>

          {/* Metrics Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <motion.div
              {...fadeUpAt(5)}
              className="bg-card border border-hairline rounded-lg p-6"
              data-testid="avg-turnaround-card"
            >
              <div className="eyebrow text-muted-foreground">AVERAGE METRICS</div>
              <div className="h-px w-10 bg-gold/50 my-4" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Avg. Turnaround Time</p>
                  <p className="font-display text-[28px] leading-none tracking-editorial text-navy-deep tnum">
                    {analytics?.avg_turnaround_time ? (
                      <>
                        {analytics.avg_turnaround_time.toFixed(1)}
                        <span className="text-[13px] font-sans text-muted-foreground ml-1.5 tracking-normal">days</span>
                      </>
                    ) : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Avg. Hours per Task</p>
                  <p className="font-display text-[28px] leading-none tracking-editorial text-navy-deep tnum">
                    {analytics?.avg_hours_per_task ? (
                      <>
                        {analytics.avg_hours_per_task.toFixed(1)}
                        <span className="text-[13px] font-sans text-muted-foreground ml-1.5 tracking-normal">hrs</span>
                      </>
                    ) : 'N/A'}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              {...fadeUpAt(6)}
              className="bg-card border border-hairline rounded-lg p-6"
              data-testid="designer-workload-card"
            >
              <div className="eyebrow text-muted-foreground">DESIGNER WORKLOAD</div>
              <div className="h-px w-10 bg-gold/50 my-4" />
              <div className="space-y-2.5">
                {analytics?.designer_workload?.length > 0 ? (
                  analytics.designer_workload.slice(0, 4).map((designer) => (
                    <div key={designer.designer_id} className="flex justify-between items-center">
                      <span className="text-[13.5px] text-navy-deep">{designer.designer_name}</span>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-[11px] eyebrow text-gold-deep">active</span>
                        <span className="font-display text-[15px] text-navy-deep tnum">{designer.active_tasks}</span>
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-[13px] text-muted-foreground">No active workload</p>
                )}
              </div>
            </motion.div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <motion.div
              {...fadeUpAt(7)}
              className="bg-card border border-hairline rounded-lg p-6"
              data-testid="category-chart"
            >
              <div className="eyebrow text-muted-foreground">CATEGORY DISTRIBUTION</div>
              <div className="h-px w-10 bg-gold/50 mt-3 mb-5" />
              {analytics?.category_distribution?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analytics.category_distribution}>
                    <CartesianGrid strokeDasharray="0" stroke="hsl(var(--hairline))" />
                    <XAxis dataKey="category" stroke="hsl(var(--muted-foreground))" fontSize={10.5} tick={{ fontFamily: 'Inter Tight' }} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10.5} tick={{ fontFamily: 'Inter Tight' }} />
                    <Tooltip
                      cursor={{ fill: 'hsl(var(--gold) / 0.08)' }}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--hairline))',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontFamily: 'Inter Tight',
                        boxShadow: '0 8px 24px -12px rgba(15,23,42,0.18)'
                      }}
                    />
                    <Bar dataKey="count" fill="hsl(var(--navy))" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-[13px] text-muted-foreground text-center py-20">No data available</p>
              )}
            </motion.div>

            <motion.div
              {...fadeUpAt(8)}
              className="bg-card border border-hairline rounded-lg p-6"
              data-testid="status-chart"
            >
              <div className="eyebrow text-muted-foreground">TASK STATUS</div>
              <div className="h-px w-10 bg-gold/50 mt-3 mb-5" />
              {analytics && pieData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={88}
                        innerRadius={60}
                        dataKey="value"
                      >
                        {pieData.map((d, index) => (
                          <Cell key={`cell-${index}`} fill={STATUS_COLORS_MAP[d.name] || CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--hairline))',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontFamily: 'Inter Tight',
                          boxShadow: '0 8px 24px -12px rgba(15,23,42,0.18)'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[11.5px]">
                    {pieData.map((d, i) => (
                      <div key={d.name} className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS_MAP[d.name] || CHART_COLORS[i % CHART_COLORS.length] }} />
                        <span className="text-muted-foreground">{d.name}</span>
                        <span className="font-display text-navy-deep tnum">{d.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-[13px] text-muted-foreground text-center py-20">No data available</p>
              )}
            </motion.div>
          </div>

          {/* Recent Completions */}
          <motion.div
            {...fadeUpAt(9)}
            className="bg-card border border-hairline rounded-lg p-6"
          >
            <div className="eyebrow text-muted-foreground">RECENT COMPLETIONS</div>
            <div className="h-px w-10 bg-gold/50 mt-3 mb-5" />
            <p className="text-[13px] text-muted-foreground">Recent completed tasks will appear here</p>
          </motion.div>
        </div>
      </div>
    </CollapsibleLayout>
  );
};

export default AdminDashboard;
